'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import type { ConversationSummaryDTO } from '@jas/shared';
import {
  deleteConversation,
  listFirstConversationsPage,
  updateConversation,
} from '@/lib/conversations';

interface Props {
  /**
   * The conversation the user is currently in. Excluded from the
   * rendered list — it's already represented by the "CURRENT" row
   * above. Null until the user sends their first message (the
   * conversation row is created lazily at that point).
   */
  currentConversationId: string | null;
  onResume: (id: string) => void;
}

const MAX_VISIBLE = 8;
// Matches UpdateConversationRequest.title's max in @jas/shared.
const TITLE_MAX = 120;

/**
 * Sidebar "RECENT" section for the in-session views (voice + chat).
 * Lists the signed-in user's recent past conversations so they can
 * jump back without opening the dedicated history drawer. Each row
 * carries a kebab (⋮) menu with Rename and Delete.
 *
 * Fetches on mount and re-fetches whenever `currentConversationId`
 * changes. Untitled conversations are filtered out — a conversation is
 * titled server-side from the user's first message, so a greeting-only
 * or abandoned session has no title and never clutters this list.
 *
 * Rendering rules:
 *   - Anonymous → render nothing (history requires sign-in)
 *   - No past titled conversations → render nothing
 *   - Otherwise → up to MAX_VISIBLE rows, newest first
 *
 * Read errors are swallowed deliberately: this is a convenience
 * surface, not the source of truth. Rename / delete are optimistic
 * and roll back on failure.
 */
export function RecentConversations({ currentConversationId, onResume }: Props) {
  const { authenticated, getAccessToken } = usePrivy();
  const [conversations, setConversations] = useState<ConversationSummaryDTO[]>([]);
  // id of the row whose kebab menu is open (only one at a time)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  // id of the row being renamed inline, plus the draft text
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  // id of a row with a rename/delete request in flight
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) {
      setConversations([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        if (!token || cancelled) return;
        const items = await listFirstConversationsPage(token);
        if (cancelled) return;
        setConversations(items);
      } catch {
        // intentional swallow — see component-level comment
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authenticated, getAccessToken, currentConversationId]);

  // Close the kebab menu on any outside click or Escape.
  useEffect(() => {
    if (!menuOpenId) return;
    const close = () => setMenuOpenId(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpenId(null);
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpenId]);

  const beginRename = useCallback((c: ConversationSummaryDTO) => {
    setMenuOpenId(null);
    setRenamingId(c.id);
    setRenameDraft(c.title ?? '');
  }, []);

  const commitRename = useCallback(
    async (id: string) => {
      const draft = renameDraft.trim().slice(0, TITLE_MAX);
      setRenamingId(null);
      const target = conversations.find((c) => c.id === id);
      if (!target) return;
      // No-op on empty input or an unchanged title — the API rejects
      // an empty title anyway (min length 1).
      if (draft.length === 0 || draft === (target.title ?? '')) return;
      const previous = target.title;
      // Optimistic: show the new title immediately.
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title: draft } : c)),
      );
      setBusyId(id);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('no access token');
        await updateConversation(token, id, { title: draft });
      } catch {
        // Rollback to the previous title.
        setConversations((prev) =>
          prev.map((c) => (c.id === id ? { ...c, title: previous } : c)),
        );
      } finally {
        setBusyId(null);
      }
    },
    [conversations, getAccessToken, renameDraft],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setMenuOpenId(null);
      const snapshot = conversations;
      // Optimistic: drop the row immediately.
      setConversations((prev) => prev.filter((c) => c.id !== id));
      setBusyId(id);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('no access token');
        await deleteConversation(token, id);
      } catch {
        // Rollback — restore the full list.
        setConversations(snapshot);
      } finally {
        setBusyId(null);
      }
    },
    [conversations, getAccessToken],
  );

  if (!authenticated) return null;

  // Exclude the active conversation (shown as CURRENT above) and any
  // untitled row — a conversation is titled only once the user has
  // sent a message, so this naturally hides greeting-only sessions
  // (including legacy ones created before lazy persistence landed).
  const past = conversations.filter(
    (c) => c.id !== currentConversationId && Boolean(c.title),
  );

  if (past.length === 0) return null;

  return (
    <div className="pt-3 pb-1">
      <p className="font-mono text-[9px] tracking-widest text-outline px-3 mb-2">RECENT</p>
      <div className="space-y-1">
        {past.slice(0, MAX_VISIBLE).map((c) => (
          <ConversationRow
            key={c.id}
            conversation={c}
            busy={busyId === c.id}
            menuOpen={menuOpenId === c.id}
            renaming={renamingId === c.id}
            renameDraft={renameDraft}
            onResume={() => onResume(c.id)}
            onToggleMenu={() =>
              setMenuOpenId((cur) => (cur === c.id ? null : c.id))
            }
            onBeginRename={() => beginRename(c)}
            onRenameDraftChange={setRenameDraft}
            onCommitRename={() => void commitRename(c.id)}
            onCancelRename={() => setRenamingId(null)}
            onDelete={() => void handleDelete(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface RowProps {
  conversation: ConversationSummaryDTO;
  busy: boolean;
  menuOpen: boolean;
  renaming: boolean;
  renameDraft: string;
  onResume: () => void;
  onToggleMenu: () => void;
  onBeginRename: () => void;
  onRenameDraftChange: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
}

function ConversationRow({
  conversation: c,
  busy,
  menuOpen,
  renaming,
  renameDraft,
  onResume,
  onToggleMenu,
  onBeginRename,
  onRenameDraftChange,
  onCommitRename,
  onCancelRename,
  onDelete,
}: RowProps) {
  // Inline rename: the title swaps for a text input. Enter commits,
  // Escape cancels, blur commits (commitRename is a no-op if unchanged).
  if (renaming) {
    return (
      <div className="px-3 py-1.5">
        <input
          autoFocus
          value={renameDraft}
          maxLength={TITLE_MAX}
          onChange={(e) => onRenameDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommitRename();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancelRename();
            }
          }}
          onBlur={onCommitRename}
          aria-label="Rename conversation"
          className="w-full bg-surface-container border border-primary text-on-surface font-mono text-[10px] tracking-widest px-2 py-1.5 focus:outline-none"
        />
      </div>
    );
  }

  return (
    <div className="group relative flex items-stretch">
      <button
        type="button"
        onClick={onResume}
        disabled={busy}
        className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2.5 hover:bg-surface-container border border-transparent hover:border-outline-variant transition-colors text-left disabled:opacity-50"
      >
        <div className="w-1 h-1 rounded-full bg-outline shrink-0" />
        <span className="font-mono text-[10px] tracking-widest text-on-surface-variant truncate">
          {c.title ?? 'Untitled'}
        </span>
      </button>

      <button
        type="button"
        aria-label="Conversation options"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onToggleMenu();
        }}
        className={`shrink-0 flex items-center px-2 text-outline hover:text-on-surface transition-opacity disabled:opacity-50 ${
          menuOpen
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
        }`}
      >
        <KebabIcon />
      </button>

      {menuOpen && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          className="absolute right-1 top-full z-20 mt-0.5 min-w-[124px] border border-outline-variant bg-surface-container-lowest shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={onBeginRename}
            className="w-full text-left px-3 py-2 font-mono text-[10px] tracking-widest text-on-surface-variant hover:bg-surface-container transition-colors"
          >
            RENAME
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={onDelete}
            className="w-full text-left px-3 py-2 font-mono text-[10px] tracking-widest text-outline hover:bg-surface-container hover:text-error transition-colors"
          >
            DELETE
          </button>
        </div>
      )}
    </div>
  );
}

function KebabIcon() {
  // Three stacked dots — inline SVG to avoid pulling an icon library
  // for one glyph (mirrors the history-trigger SVG in voice-panel).
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}
